import React, { useEffect, useRef, useState } from 'react';
import '../components/Carousel.css';

const Carousel = ({ images, autoPlay = true, autoPlayInterval = 4000 }) => {
  const trackRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  const scrollToSlide = (index) => {
    const track = trackRef.current;
    if (!track) return;

    const slide = track.children[index];
    if (slide) {
      const scrollLeft = slide.offsetLeft - track.offsetLeft;
      track.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      setCurrentIndex(index);
    }
  };

  const nextSlide = () => {
    const nextIndex = (currentIndex + 1) % images.length;
    scrollToSlide(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    scrollToSlide(prevIndex);
  };

  const startAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(nextSlide, autoPlayInterval);
    setIsPlaying(true);
  };

  const stopAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextSlide();
      }
    };

    const carousel = trackRef.current?.closest('.carousel');
    if (carousel) {
      carousel.addEventListener('keydown', handleKeyDown);
      return () => carousel.removeEventListener('keydown', handleKeyDown);
    }
  }, [currentIndex]);

  // Auto-play functionality
  useEffect(() => {
    if (isPlaying && autoPlay) {
      startAutoPlay();
    } else {
      stopAutoPlay();
    }

    return () => stopAutoPlay();
  }, [isPlaying, autoPlay, autoPlayInterval]);

  // Pause on hover
  const handleMouseEnter = () => {
    if (autoPlay) stopAutoPlay();
  };

  const handleMouseLeave = () => {
    if (autoPlay) startAutoPlay();
  };

  return (
    <div 
      className="carousel" 
      aria-label="galerie"
      tabIndex="0"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button 
        className="nav prev" 
        aria-label="Précédent"
        onClick={prevSlide}
        onFocus={stopAutoPlay}
        onBlur={() => autoPlay && startAutoPlay()}
      >
        ‹
      </button>
      
      <div className="track" ref={trackRef} aria-live="polite">
        {images.map((image, index) => (
          <div key={index} className="slide">
            <img 
              src={image.src} 
              alt={image.alt} 
              loading="lazy"
              draggable="false"
            />
          </div>
        ))}
      </div>
      
      <button 
        className="nav next" 
        aria-label="Suivant"
        onClick={nextSlide}
        onFocus={stopAutoPlay}
        onBlur={() => autoPlay && startAutoPlay()}
      >
        ›
      </button>

      {/* Visual indicator for screen readers */}
      <div className="sr-only" aria-live="polite">
        Image {currentIndex + 1} sur {images.length}
      </div>

      {/* Dot indicators */}
      <div className="carousel-indicators">
        {images.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => scrollToSlide(index)}
            aria-label={`Aller à l'image ${index + 1}`}
            onFocus={stopAutoPlay}
            onBlur={() => autoPlay && startAutoPlay()}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;